import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Fornecedor, FornecedorFromDB, FornecedorFormData } from '@/types/fornecedor';
import { useToast } from '@/hooks/use-toast';
import { useClientFilter } from '@/hooks/useClientFilter';

const convertToFornecedor = (fornecedorFromDB: FornecedorFromDB): Fornecedor => {
  return {
    id: fornecedorFromDB.id,
    nome: fornecedorFromDB.nome,
    cnpj: fornecedorFromDB.cnpj || undefined,
    contato_responsavel: fornecedorFromDB.contato_responsavel || undefined,
    telefone_contato: fornecedorFromDB.telefone_contato || undefined,
    email_contato: fornecedorFromDB.email_contato || undefined,
    endereco: fornecedorFromDB.endereco || undefined,
    site: fornecedorFromDB.site || undefined,
    observacoes: fornecedorFromDB.observacoes || undefined,
    client_id: fornecedorFromDB.client_id || undefined,
    created_at: fornecedorFromDB.created_at,
    updated_at: fornecedorFromDB.updated_at,
  };
};

export const useFornecedoresSecure = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { 
    currentClientId, 
    isAdmin, 
    canAccessRecord,
    validateClientData,
    applyClientFilter 
  } = useClientFilter();

  // Fetch fornecedores with client filtering and frontend security validation
  const {
    data: fornecedores = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['fornecedores-secure', currentClientId],
    queryFn: async () => {
      let query = supabase
        .from('fornecedores')
        .select('*');

      // Apply client filter based on user permissions
      query = applyClientFilter(query);
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Frontend security validation - filter based on user access rights
      const filteredData = [];
      for (const fornecedor of data as FornecedorFromDB[]) {
        const hasAccess = await canAccessRecord(fornecedor.client_id);
        if (hasAccess) {
          filteredData.push(fornecedor);
        }
      }
      
      return filteredData.map(convertToFornecedor);
    },
    enabled: !!currentClientId || isAdmin
  });

  const createFornecedor = useMutation({
    mutationFn: async (fornecedorData: FornecedorFormData) => {
      // Validate client data before inserting
      const validation = validateClientData(fornecedorData);
      if (!validation.isValid) {
        throw new Error(validation.error || 'Dados de cliente inválidos');
      }

      const { data, error } = await supabase
        .from('fornecedores')
        .insert([validation.data])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fornecedores-secure'] });
      toast({
        title: "Sucesso",
        description: "Fornecedor criado com sucesso!"
      });
    },
    onError: (error) => {
      console.error('Error creating fornecedor:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar fornecedor: " + error.message,
        variant: "destructive"
      });
    }
  });

  const updateFornecedor = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FornecedorFormData> }) => {
      // First verify the user can access this fornecedor
      const { data: existingFornecedor } = await supabase
        .from('fornecedores')
        .select('client_id')
        .eq('id', id)
        .single();

      if (!existingFornecedor || !(await canAccessRecord(existingFornecedor.client_id))) {
        throw new Error('Acesso negado: você não tem permissão para editar este fornecedor');
      }

      // Validate the updated data
      const validation = validateClientData({ ...updates, client_id: existingFornecedor.client_id });
      if (!validation.isValid) {
        throw new Error(validation.error || 'Dados de cliente inválidos');
      }

      const { data, error } = await supabase
        .from('fornecedores')
        .update(validation.data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fornecedores-secure'] });
      toast({
        title: "Sucesso",
        description: "Fornecedor atualizado com sucesso!"
      });
    },
    onError: (error) => {
      console.error('Error updating fornecedor:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar fornecedor: " + error.message,
        variant: "destructive"
      });
    }
  });

  const deleteFornecedor = useMutation({
    mutationFn: async (id: string) => {
      // First verify the user can access this fornecedor
      const { data: existingFornecedor } = await supabase
        .from('fornecedores')
        .select('client_id')
        .eq('id', id)
        .single();

      if (!existingFornecedor || !(await canAccessRecord(existingFornecedor.client_id))) {
        throw new Error('Acesso negado: você não tem permissão para excluir este fornecedor');
      }

      const { error } = await supabase
        .from('fornecedores')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fornecedores-secure'] });
      toast({
        title: "Sucesso",
        description: "Fornecedor excluído com sucesso!"
      });
    },
    onError: (error) => {
      console.error('Error deleting fornecedor:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir fornecedor: " + error.message,
        variant: "destructive"
      });
    }
  });

  return {
    fornecedores,
    isLoading,
    error,
    createFornecedor,
    updateFornecedor,
    deleteFornecedor,
    currentClientId,
    isAdmin
  };
};